import {
  useState, useEffect, useMemo, useRef, useCallback,
} from 'react';
import axios from 'axios';

import { request } from '../helpers/requests';

function useProjectCostcodesFetch({
  projectIds = [],
  includeCategoryIds = false,
  columns = [],
  active,
  onFetched,
} = {}) {
  const projectIdsKey = useMemo(
    () => [...projectIds]
      .filter(Boolean)
      .sort((a, b) => String(a).localeCompare(String(b)))
      .join(','),
    [projectIds],
  );

  const [projectCostcodes, setProjectCostcodes] = useState([]);
  // True on first paint when projects are requested so costcodesLoading does not
  // briefly look "done" before the fetch effect runs.
  const [loading, setLoading] = useState(() => Boolean(projectIdsKey));
  const fetchedProjectIds = useRef(new Set());
  // Claimed as soon as we intend to fetch, so pending ids count as loading even
  // before setLoading(true) from the async function flushes.
  const claimedProjectIds = useRef(new Set());
  const inflightCount = useRef(0);

  const columnsKey = useMemo(
    () => [...(columns || [])]
      .sort((a, b) => String(a).localeCompare(String(b)))
      .join(','),
    [columns],
  );

  const queryKey = `${columnsKey}|${includeCategoryIds}|${active}`;
  const queryKeyRef = useRef(queryKey);

  const fetchProjects = useCallback(async (idsToFetch) => {
    const newIds = idsToFetch.filter((id) => id && !fetchedProjectIds.current.has(id));
    if (!newIds.length) return;

    newIds.forEach((id) => {
      fetchedProjectIds.current.add(id);
      claimedProjectIds.current.add(id);
    });
    inflightCount.current += 1;
    setLoading(true);

    try {
      const res = await request({
        call: axios.post('/costcodes/projects', {
          projectIds: newIds,
          ...(columnsKey && { columns: columnsKey.split(',') }),
          ...(includeCategoryIds && { includeCategoryIds: true }),
          ...(active !== undefined && { active }),
        }),
        errMsg: 'Could not get project costcodes',
        hideSuccessToast: true,
      });

      if (!res?.data) {
        newIds.forEach((id) => fetchedProjectIds.current.delete(id));
        return;
      }

      // Apply data before clearing loading so validation never sees "done + empty".
      setProjectCostcodes((prev) => [...prev, ...res.data]);
      onFetched?.(res.data);
    } finally {
      inflightCount.current = Math.max(0, inflightCount.current - 1);
      if (inflightCount.current === 0) {
        setLoading(false);
      }
    }
  }, [columnsKey, includeCategoryIds, active, onFetched]);

  const fetchNewIds = useCallback(() => {
    if (queryKeyRef.current !== queryKey) {
      queryKeyRef.current = queryKey;
      fetchedProjectIds.current.clear();
      claimedProjectIds.current.clear();
      setProjectCostcodes([]);
    }
    if (!projectIdsKey) {
      setLoading(false);
      return;
    }
    fetchProjects(projectIdsKey.split(','));
  }, [fetchProjects, projectIdsKey, queryKey]);

  const fetchProjectCostcodes = useCallback((id) => (
    fetchProjects([id])
  ), [fetchProjects]);

  // Discards what was already fetched, for callers that mutate cost codes.
  const refresh = useCallback(() => {
    fetchedProjectIds.current.clear();
    setProjectCostcodes([]);
    fetchNewIds();
  }, [fetchNewIds]);

  useEffect(() => {
    fetchNewIds();
  }, [fetchNewIds]);

  // Cover the paint between projectIds updating and the fetch effect claiming them.
  const hasUnfetchedProjects = projectIdsKey
    .split(',')
    .filter(Boolean)
    .some((id) => !claimedProjectIds.current.has(id));

  return {
    projectCostcodes,
    loading: loading || hasUnfetchedProjects,
    fetchNewIds,
    fetchProjectCostcodes,
    refresh,
  };
}

export default useProjectCostcodesFetch;
